import {
  Dialog,
  PlusIcon,
  getFlightServices,
  NumberField,
  setFlightService,
  type FlightService,
  type FlightsOverview,
} from '@trippath/shared';
import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ModeIcon } from '../../components/icons/ModeIcon';

/** A monthly allowance this high is none: as many requests as wanted. */
export const UNLIMITED = 1_000_000;

/** An instant as the viewer's clock says it: `14.10.2026, 10:13`. */
function when(at: string, lang: string): string {
  return new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(at),
  );
}

/** An eye, struck through while what it shows is shown. */
function EyeIcon({ shown }: { shown: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
      {shown && <path d="M3 3l18 18" />}
    </svg>
  );
}

/** A gear: a thing's settings. */
function GearIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}

/** What a service's settings are changed to: a new key only when one is typed. */
interface Change {
  apiKey?: string;
  monthlyLimit: number;
  enabled: boolean;
  added: boolean;
}

/** A service's settings: its key, shown when asked; its monthly allowance or none; its use. */
function SettingsForm({
  service,
  onClose,
  onSave,
}: {
  service: FlightService;
  onClose: () => void;
  onSave: (change: Change) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [key, setKey] = useState('');
  const [shown, setShown] = useState(false);
  const [unlimited, setUnlimited] = useState(service.monthlyLimit >= UNLIMITED);
  const [limit, setLimit] = useState<number | null>(unlimited ? null : service.monthlyLimit);
  const [enabled, setEnabled] = useState(service.enabled);
  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          ...(key.trim() === '' ? {} : { apiKey: key }),
          monthlyLimit: unlimited ? UNLIMITED : (limit ?? 0),
          enabled,
          added: true,
        });
      }}
    >
      <div className="field">
        <label className="field-label" htmlFor={`${id}-key`}>
          {t('flightApis.key')}
        </label>
        <div className="key-field">
          <input
            id={`${id}-key`}
            className="text-input mono"
            type={shown ? 'text' : 'password'}
            autoComplete="off"
            placeholder={service.keyEnd === null ? '' : `••••${service.keyEnd}`}
            value={key}
            onChange={(event) => {
              setKey(event.target.value);
            }}
          />
          <button
            type="button"
            className="icon-button"
            aria-pressed={shown}
            aria-label={t('flightApis.showKey')}
            title={t('flightApis.showKey')}
            onClick={() => {
              setShown(!shown);
            }}
          >
            <EyeIcon shown={shown} />
          </button>
        </div>
        <a className="text-link quiet" href={service.signup} target="_blank" rel="noreferrer">
          {t('flightApis.signup', { name: service.label })} ↗
        </a>
      </div>
      <div className="form-row">
        {!unlimited && (
          <NumberField
            label={t('flightApis.limit')}
            value={limit}
            min={0}
            max={UNLIMITED - 1}
            onChange={setLimit}
          />
        )}
        <label className="check flight-service-unlimited">
          <input
            type="checkbox"
            checked={unlimited}
            onChange={(event) => {
              setUnlimited(event.target.checked);
            }}
          />
          {t('flightApis.unlimited')}
        </label>
      </div>
      <label className="check">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => {
            setEnabled(event.target.checked);
          }}
        />
        {t('flightApis.enabled')}
      </label>
      <div className="actions">
        <button type="submit" className="button button-primary">
          {t('flightApis.save')}
        </button>
        <button type="button" className="button" onClick={onClose}>
          {t('flightApis.cancel')}
        </button>
        {service.added && (
          <button
            type="button"
            className="button button-quiet"
            onClick={() => {
              onSave({
                apiKey: '',
                monthlyLimit: service.monthlyLimit,
                enabled: false,
                added: false,
              });
            }}
          >
            {t('flightApis.remove')}
          </button>
        )}
      </div>
    </form>
  );
}

/** One service in a row: its order, kind, name and state, this month's use, its settings. */
function ServiceRow({
  service,
  first,
  last,
  dragged,
  over,
  onSettings,
  onMove,
  onDrag,
}: {
  service: FlightService;
  first: boolean;
  last: boolean;
  /** It is being dragged; another is dragged over it. */
  dragged: boolean;
  over: boolean;
  onSettings: () => void;
  onMove: (by: -1 | 1) => void;
  /** Where a drag of it starts, goes over it, is dropped on it, or ends. */
  onDrag: (event: 'start' | 'over' | 'drop' | 'end') => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { used, monthlyLimit } = service;
  const unlimited = monthlyLimit >= UNLIMITED;
  const share = unlimited ? 0 : monthlyLimit > 0 ? Math.min(1, used.month / monthlyLimit) : 1;
  const state = service.keyEnd === null ? 'noKey' : service.enabled ? 'on' : 'off';

  return (
    <li
      className={`flight-service${dragged ? ' flight-service-dragged' : ''}${over ? ' flight-service-over' : ''}`}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', service.name);
        onDrag('start');
      }}
      onDragOver={(event) => {
        event.preventDefault();
        onDrag('over');
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrag('drop');
      }}
      onDragEnd={() => {
        onDrag('end');
      }}
    >
      {/* Dragged into its place; on the keyboard, Alt with an arrow. */}
      <button
        type="button"
        className="icon-button icon-button-small flight-service-grip"
        aria-label={t('flightApis.move', { name: service.label })}
        title={t('flightApis.move', { name: service.label })}
        aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
        onKeyDown={(event) => {
          const by = { ArrowUp: -1, ArrowDown: 1 }[event.key];
          if (!event.altKey || by === undefined) return;
          event.preventDefault();
          if ((by === -1 && !first) || (by === 1 && !last)) onMove(by === -1 ? -1 : 1);
        }}
      >
        ⠿
      </button>
      {/* Green while asked: its state said by its mark, in words when pointed at. */}
      <span
        className={`flight-service-kind flight-service-${state}`}
        title={`${t('flightApis.flights')} · ${t(`flightApis.state.${state}`)}`}
      >
        <ModeIcon mode="flight" size={18} />
        <span className="visually-hidden">{t(`flightApis.state.${state}`)}</span>
      </span>
      <div className="flight-service-name">
        <h3>
          <span className="flight-service-order mono" aria-hidden="true">
            {service.position + 1}
          </span>
          {service.label}
        </h3>
        <p className="quiet">
          {state === 'noKey'
            ? t('flightApis.state.noKey')
            : t(`flightApis.what.${service.name}`)}
        </p>
        {service.lastError !== '' && (
          <p className="data-step-failed">
            {t('flightApis.lastError', { error: service.lastError })}
          </p>
        )}
      </div>
      <div
        className="flight-service-use"
        title={t('flightApis.used', {
          day: used.day.toLocaleString(lang),
          year: used.year.toLocaleString(lang),
        })}
      >
        <p className="mono">
          <strong>{used.month.toLocaleString(lang)}</strong>
          <span className="quiet">
            {' / '}
            {unlimited ? '∞' : monthlyLimit.toLocaleString(lang)} {t('flightApis.aMonth')}
          </span>
        </p>
        <div className="flight-service-bar" aria-hidden="true">
          <span style={{ width: `${String(share * 100)}%` }} />
        </div>
      </div>
      <button
        type="button"
        className="icon-button"
        aria-label={t('flightApis.settings', { name: service.label })}
        title={t('flightApis.settings', { name: service.label })}
        onClick={onSettings}
      >
        <GearIcon />
      </button>
    </li>
  );
}

/**
 * The services TripPath asks, in turn — a flight by its number and day from the next when one
 * fails, has no key or has used its month — each in a row with this month's use, its settings a
 * press away; and the latest requests, each found, not found, failed or answered from what was
 * kept.
 */
export function FlightServices() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [overview, setOverview] = useState<FlightsOverview | null>(null);
  const [failed, setFailed] = useState(false);
  const [dragging, setDragging] = useState<number | null>(null);
  const [overAt, setOverAt] = useState<number | null>(null);
  const [editing, setEditing] = useState<FlightService | null>(null);
  const [choosing, setChoosing] = useState(false);

  const load = useCallback(() => {
    getFlightServices().then(setOverview, () => {
      setFailed(true);
    });
  }, []);
  useEffect(load, [load]);

  if (overview === null) {
    return failed ? (
      <p className="form-failed" role="alert">
        {t('flightApis.failed')}
      </p>
    ) : null;
  }
  const change = (name: string, to: Parameters<typeof setFlightService>[1]) => {
    setFailed(false);
    setFlightService(name, to).then(load, () => {
      setFailed(true);
    });
  };
  const { requests } = overview;
  // Only the services added are asked and shown; the rest are choices to add.
  const services = overview.services.filter((s) => s.added);
  const choices = overview.services.filter((s) => !s.added);
  // A service moved to another's place: the others in between move along.
  const reorder = (from: number, to: number) => {
    const order = services.map((s) => s.name);
    order.splice(to, 0, ...order.splice(from, 1));
    setFailed(false);
    Promise.all(order.map((name, position) => setFlightService(name, { position }))).then(
      load,
      () => {
        setFailed(true);
      },
    );
  };

  return (
    <div className="data-card">
      <p className="quiet">{t('flightApis.lead')}</p>
      {failed && (
        <p className="form-failed" role="alert">
          {t('flightApis.failed')}
        </p>
      )}
      {services.length === 0 && (
        <p className="flight-services-none" role="status">
          {t('flightApis.none')}
        </p>
      )}
      <ol className="flight-services">
        {services.map((service, at) => (
          <ServiceRow
            key={service.name}
            service={service}
            first={at === 0}
            last={at === services.length - 1}
            dragged={dragging === at}
            over={overAt === at && dragging !== at}
            onSettings={() => {
              setEditing(service);
            }}
            onMove={(by) => {
              reorder(at, at + by);
            }}
            onDrag={(event) => {
              if (event === 'start') setDragging(at);
              else if (event === 'over') setOverAt(at);
              else {
                if (event === 'drop' && dragging !== null && dragging !== at)
                  reorder(dragging, at);
                setDragging(null);
                setOverAt(null);
              }
            }}
          />
        ))}
      </ol>
      {choices.length > 0 && (
        <button
          type="button"
          className="button flight-service-add"
          onClick={() => {
            setChoosing(true);
          }}
        >
          <PlusIcon /> {t('flightApis.add')}
        </button>
      )}
      {(choosing || editing) && (
        // One dialog: a service chosen to add shows its settings in place, focus kept in it.
        <Dialog
          open
          title={editing ? editing.label : t('flightApis.add')}
          onClose={() => {
            setChoosing(false);
            setEditing(null);
          }}
        >
          {editing ? (
            <SettingsForm
              service={editing}
              onClose={() => {
                setChoosing(false);
                setEditing(null);
              }}
              onSave={(to) => {
                setChoosing(false);
                setEditing(null);
                change(editing.name, to);
              }}
            />
          ) : (
            <ul className="flight-service-choices">
              {choices.map((choice) => (
                <li key={choice.name}>
                  <span className="flight-service-kind">
                    <ModeIcon mode="flight" size={18} />
                  </span>
                  <div className="flight-service-name">
                    <h3>{choice.label}</h3>
                    <p className="quiet">{t(`flightApis.what.${choice.name}`)}</p>
                  </div>
                  <button
                    type="button"
                    className="button button-small"
                    aria-label={t('flightApis.addOne', { name: choice.label })}
                    onClick={() => {
                      setEditing({ ...choice, enabled: true });
                    }}
                  >
                    {t('flightApis.addThis')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Dialog>
      )}
      <h3 className="flight-requests-head">{t('flightApis.requests')}</h3>
      {requests.length === 0 ? (
        <p className="quiet">{t('flightApis.noRequests')}</p>
      ) : (
        <div className="flight-requests">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t('flightApis.at')}</th>
                <th scope="col">{t('flightApis.flight')}</th>
                <th scope="col">{t('flightApis.service')}</th>
                <th scope="col">{t('flightApis.outcome')}</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request, at) => (
                <tr key={at}>
                  <td data-label={t('flightApis.at')}>{when(request.at, lang)}</td>
                  <td data-label={t('flightApis.flight')} className="mono">
                    {request.number} · {request.day}
                  </td>
                  <td data-label={t('flightApis.service')}>
                    {services.find((s) => s.name === request.service)?.label ??
                      t('flightApis.cache')}
                  </td>
                  <td data-label={t('flightApis.outcome')}>
                    <span
                      className={`data-status data-status-${
                        request.outcome === 'failed'
                          ? 'failed'
                          : request.outcome === 'none'
                            ? 'off'
                            : 'ok'
                      }`}
                    >
                      {t(`flightApis.outcomes.${request.outcome}`)}
                    </span>
                    {request.error !== '' && (
                      <span className="data-step-failed flight-request-error">
                        {request.error}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
